import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { userApi, User, UpdateUserData } from '../api/userApi';
import { billingStatusKey } from './useBilling';

// Hook to get all users
export const useUsers = () => {
  const query = useQuery({
    queryKey: ['users'],
    queryFn: userApi.getAllUsers,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  return {
    ...query,
    data: query.data?.users,
    ownerId: query.data?.ownerId ?? null,
  };
};

// Hook to get user by ID
export const useUser = (id: string) => {
  return useQuery<User, Error>({
    queryKey: ['users', id],
    queryFn: () => userApi.getUserById(id),
    enabled: !!id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Create user mutation
export const useCreateUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: userApi.createUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: billingStatusKey });
    },
  });
};

// Update user mutation
export const useUpdateUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, userData }: { id: string; userData: UpdateUserData }) =>
      userApi.updateUser(id, userData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: billingStatusKey });
    },
  });
};

// Delete user mutation
export const useDeleteUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: userApi.deleteUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: billingStatusKey });
    },
  });
};
